// View 1 — Users table.
//
// Per design/UI : UX v1.0/for-engineering-users/specs/users-strings.md.
// Layout: PageHeader → stat cards → filter row → table → footer count.
//
// MR1 scope:
//   - Status chips + role select + search filter
//   - Stat cards (clicking "Active"/"Pending" chips also filters)
//   - Sortable-by-default table (no per-column sort UI yet)
//   - Bulk bar appears when ≥1 row selected; "Change role" is the only
//     bulk action wired (Suspend + Remove route through the detail drawer)
//   - Row click → opens UserDetailDrawer
//   - "+ Invite user" → opens InviteUserDrawer
//   - "Manage roles" → navigates to /admin/team/roles

import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  RoleBadge,
  StatCard,
  TextInput,
} from '@gc-coach/design-system';
import { ALL_ROLES, type RoleKey, type UserStatusFilter, type UserSummary } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { roleLabel } from '../shell/roleLabels.js';
import { useI18n } from '../i18n/index.js';
import { InviteUserDrawer } from './InviteUserDrawer.js';
import { UserDetailDrawer } from './UserDetailDrawer.js';
import { useUsers } from './useUsers.js';
import { formatRelativeTime } from './time.js';
import styles from './UsersPage.module.css';

export function UsersPage() {
  const navigate = useNavigate();
  const { t, tt } = useI18n();

  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleKey | ''>('');
  const [q, setQ] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(roleFilter ? { role: roleFilter } : {}),
      ...(q.trim() ? { q } : {}),
    }),
    [statusFilter, roleFilter, q],
  );

  const { data, isLoading, refetch } = useUsers(query);
  const users = data?.users ?? [];
  const counts = data?.counts ?? {
    total: 0,
    active: 0,
    invited: 0,
    suspended: 0,
    removed: 0,
    mfaEnabled: 0,
    mfaPending: 0,
  };

  const subText = tt('users.subTemplate', {
    active: counts.active,
    invited: counts.invited,
    suspended: counts.suspended,
  });

  return (
    <AdminShell
      activeKey="users"
      breadcrumb={[t('users.breadcrumbTeam'), t('users.breadcrumbUsers')]}
      state={isLoading && !data ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={t('users.title')}
        sub={subText}
        actions={
          <>
            <Button kind="ghost" onClick={() => navigate('/admin/team/roles')}>
              {t('users.manageRolesButton')}
            </Button>
            <Button kind="primary" onClick={() => setShowInvite(true)}>
              {t('users.inviteButton')}
            </Button>
          </>
        }
      />

      {/* Stat cards */}
      <div className={styles.statRow}>
        <StatCard
          label={t('users.statUsers')}
          value={counts.total.toString()}
          onClick={() => setStatusFilter('all')}
        />
        <StatCard
          label={t('users.statActive')}
          value={counts.active.toString()}
          onClick={() => setStatusFilter('active')}
        />
        <StatCard
          label={t('users.statPending')}
          value={counts.invited.toString()}
          onClick={() => setStatusFilter('invited')}
        />
        <StatCard
          label={t('users.statMfa')}
          value={counts.mfaEnabled.toString()}
          caption={
            counts.mfaPending > 0
              ? tt('users.statMfaPendingTemplate', { count: counts.mfaPending })
              : undefined
          }
        />
      </div>

      {/* Filters */}
      <div className={styles.filterRow}>
        <div className={styles.chips} role="group" aria-label="Status filter">
          <FilterChip
            label={tt('users.chipAllTemplate', { n: counts.total - counts.removed })}
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
          <FilterChip
            label={tt('users.chipActiveTemplate', { n: counts.active })}
            active={statusFilter === 'active'}
            onClick={() => setStatusFilter('active')}
          />
          <FilterChip
            label={tt('users.chipInvitedTemplate', { n: counts.invited })}
            active={statusFilter === 'invited'}
            onClick={() => setStatusFilter('invited')}
          />
          <FilterChip
            label={tt('users.chipSuspendedTemplate', { n: counts.suspended })}
            active={statusFilter === 'suspended'}
            onClick={() => setStatusFilter('suspended')}
          />
          <FilterChip
            label={tt('users.chipRemovedTemplate', { n: counts.removed })}
            active={statusFilter === 'removed'}
            onClick={() => setStatusFilter('removed')}
          />
        </div>

        <div className={styles.filterRight}>
          <label className={styles.roleFilter}>
            <span className={styles.roleFilterLabel}>{t('users.roleFilterLabel')}</span>
            <select
              className={styles.roleSelect}
              value={roleFilter}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setRoleFilter(e.target.value as RoleKey | '')
              }
            >
              <option value="">{t('users.roleFilterAll')}</option>
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r, t)}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.search}>
            <TextInput
              type="search"
              placeholder={t('users.searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label={t('users.searchPlaceholder')}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.colUser}>{t('users.colUser')}</th>
              <th>{t('users.colRole')}</th>
              <th>{t('users.colRegion')}</th>
              <th>{t('users.colMfa')}</th>
              <th>{t('users.colLastSeen')}</th>
              <th>{t('users.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  <strong>{t('users.emptyTitle')}</strong>
                  <span>{t('users.emptyBody')}</span>
                </td>
              </tr>
            )}
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                onClick={() => setSelectedId(user.id)}
              />
            ))}
          </tbody>
        </table>
        <footer className={styles.tableFooter}>
          {tt('users.footerCountTemplate', { n: users.length })}
        </footer>
      </div>

      {showInvite && (
        <InviteUserDrawer
          onClose={() => setShowInvite(false)}
          onInvited={() => {
            setShowInvite(false);
            void refetch();
          }}
        />
      )}

      {selectedId && (
        <UserDetailDrawer
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            void refetch();
          }}
          onRemoved={() => {
            setSelectedId(null);
            void refetch();
          }}
        />
      )}
    </AdminShell>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function UserRow({ user, onClick }: { user: UserSummary; onClick: () => void }) {
  const { t } = useI18n();
  const primaryRole: RoleKey = user.roles[0] ?? 'read_only';
  const displayName =
    user.status === 'invited' ? t('users.invitedRowName') : user.displayName;
  return (
    <tr className={styles.row} onClick={onClick} tabIndex={0}>
      <td className={styles.cellUser}>
        <Avatar
          name={user.displayName}
          email={user.email}
          size="sm"
          variant={user.status === 'invited' ? 'dashed' : 'filled'}
        />
        <span className={styles.userText}>
          <span
            className={[styles.userName, user.status === 'invited' ? styles.userNameItalic : null]
              .filter(Boolean)
              .join(' ')}
          >
            {displayName}
          </span>
          <span className={styles.userEmail}>{user.email}</span>
        </span>
      </td>
      <td>
        <RoleBadge role={primaryRole} label={roleLabel(primaryRole, t)} />
      </td>
      <td className={styles.cellMono}>{user.region.toUpperCase()}</td>
      <td className={user.mfaEnabled ? styles.mfaOn : styles.mfaOff}>
        {user.mfaEnabled ? t('users.mfaOn') : t('users.mfaOff')}
      </td>
      <td className={styles.cellMuted}>
        {user.lastSeenAt ? formatRelativeTime(user.lastSeenAt) : '—'}
      </td>
      <td className={statusClass(user.status, styles)}>{statusText(user.status, t)}</td>
    </tr>
  );
}

function statusClass(s: UserSummary['status'], cls: Record<string, string>): string {
  if (s === 'active') return cls['statusActive'] ?? '';
  if (s === 'invited') return cls['statusInvited'] ?? '';
  return cls['statusSuspended'] ?? '';
}

function statusText(s: UserSummary['status'], t: (k: string) => string): string {
  if (s === 'active') return t('users.statusActive');
  if (s === 'invited') return t('users.statusInvited');
  return t('users.statusSuspended');
}

// ─── Filter chip ─────────────────────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[styles.chip, active ? styles.chipActive : null].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
